import {
  isDeceasedOrDecedent,
  isBusinessOwnerOrControlPerson,
} from "./memberLinks";
import {
  getWaivedMemberFields,
  getBeneficiaryMemberIds,
  getOwnerEquivalentMemberIds,
  getTrustedContactMemberIds,
} from "./memberAccountRoles";
import { showMaritalStatus, showIdInfo } from "./brokerageFields";

const PERSONAL_DETAILS_TOKENS = ["personal_info", "is_deceased", "date_of_death"];

const isEmptyValue = (value) =>
  value === null || value === undefined || value === "";

const parsePersonalInfo = (member) => {
  const raw = member?.personal_info;
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return raw;
};

const getHomeAddress = (personalInfo) => {
  const addresses = Array.isArray(personalInfo.address)
    ? personalInfo.address
    : [];
  return (
    addresses.find(
      (addr) => String(addr?.type || "").toLowerCase() === "home",
    ) || {}
  );
};

const labelForToken = (token) => {
  if (token === "personal_info") return "Personal Details";
  return token.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

const requiredNonEmailPersonalFields = ({
  deceased,
  isOwner,
  brokerageFlags,
  waived,
}) => {
  let base;
  if (deceased) {
    base = ["first_name", "last_name", "ssn_or_tin", "dob"];
  } else if (isOwner) {
    base = [
      "title",
      "first_name",
      "last_name",
      ...(showMaritalStatus(brokerageFlags) ? ["marital_status"] : []),
      "ssn_or_tin",
      "dob",
      "country_of_citizenship",
    ];
  } else {
    base = [
      "first_name",
      "last_name",
      "relationship",
      "ssn_or_tin",
      "dob",
      "country_of_citizenship",
    ];
  }
  return base.filter((field) => !waived.has(field));
};

/** Build advisor pending pills for one member, splitting Email and Phone out. */
export const getMemberMissingPills = ({
  member,
  memberId,
  fields,
  household,
  brokerageFlags,
}) => {
  const tokenList = (Array.isArray(fields) ? fields : []).filter(
    (f) => typeof f === "string",
  );
  const idInfoAllowed = showIdInfo(brokerageFlags);

  const personalInfo = parsePersonalInfo(member);
  const homeAddress = getHomeAddress(personalInfo);
  const id = String(memberId);
  const accounts = household?.accounts;

  const deceased = isDeceasedOrDecedent(member, accounts);
  const waived =
    getWaivedMemberFields(household, memberId, brokerageFlags) || new Set();
  const isOwner =
    getOwnerEquivalentMemberIds(household).has(id) ||
    isBusinessOwnerOrControlPerson(accounts, memberId);
  const isBeneficiary = getBeneficiaryMemberIds(household).has(id);
  const isTrustedContact = getTrustedContactMemberIds(household).has(id);

  const buildPersonalPills = () => {
    const emailRequired = !deceased && !waived.has("email");
    const emailMissing = emailRequired && isEmptyValue(personalInfo.email);

    let otherPersonalMissing;
    if (!emailMissing) {
      otherPersonalMissing = true;
    } else {
      otherPersonalMissing = requiredNonEmailPersonalFields({
        deceased,
        isOwner,
        brokerageFlags,
        waived,
      }).some((field) => isEmptyValue(personalInfo[field]));
    }

    const pills = [];
    if (otherPersonalMissing) {
      pills.push({
        key: "personal_info",
        label: "Personal Details",
        editorField: "personal_info",
      });
    }
    if (emailMissing) {
      pills.push({ key: "email", label: "Email", editorField: "email" });
    }
    if (pills.length === 0) {
      pills.push({
        key: "personal_info",
        label: "Personal Details",
        editorField: "personal_info",
      });
    }
    return pills;
  };

  const buildAddressPills = () => {
    const phoneRequired = isOwner || isTrustedContact || !isBeneficiary;
    const phoneMissing = phoneRequired && isEmptyValue(homeAddress.phone);

    let otherAddressMissing;
    if (!phoneMissing) {
      otherAddressMissing = true;
    } else {
      otherAddressMissing = [
        "address_line_1",
        "city",
        "province",
        "postal_code",
        "country",
      ].some((field) => isEmptyValue(homeAddress[field]));
    }

    const pills = [];
    if (otherAddressMissing) {
      pills.push({ key: "address", label: "Address", editorField: "address" });
    }
    if (phoneMissing) {
      pills.push({ key: "phone", label: "Phone", editorField: "phone" });
    }
    if (pills.length === 0) {
      pills.push({ key: "address", label: "Address", editorField: "address" });
    }
    return pills;
  };

  const pills = [];
  let personalHandled = false;
  tokenList.forEach((token) => {
    if (token === "id_info" && !idInfoAllowed) return;
    if (PERSONAL_DETAILS_TOKENS.includes(token)) {
      if (personalHandled) return;
      personalHandled = true;
      pills.push(...buildPersonalPills());
    } else if (token === "address") {
      pills.push(...buildAddressPills());
    } else {
      pills.push({
        key: token,
        label: labelForToken(token),
        editorField: token,
      });
    }
  });

  return pills;
};
